import { Link } from "react-router-dom";
import { BadgeCheck, Clock, MapPin, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BUSINESS } from "@/lib/site";
import { CategoryLinks } from "@/components/InlineLinks";
import { track } from "@/lib/analytics";

const TRUST = [
  [BadgeCheck, "ABCA licensed"],
  [Clock, "Open daily from 10 AM"],
  [ShieldCheck, "21+ with ID"],
  [MapPin, "Upstairs at 1738 14th St NW"],
];

export const Hero = () => (
  <section className="on-dark grain relative flex min-h-[78vh] items-center overflow-hidden bg-brand-ink" data-testid="home-hero">
    <img src="/img/store-2.webp" alt="Inside District Gaslight: the illuminated GasLight sign on a red wall, glass display counter, brick wall and yellow stools" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover object-[70%_center] opacity-90" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent max-md:bg-black/60" aria-hidden="true" />
    <div className="relative mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow-lime animate-fade-up">Washington, DC · ABCA licensed</p>
        <h1 className="mt-5 animate-fade-up font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl" style={{ animationDelay: "0.1s" }}>
          Your Medical Cannabis Dispensary on <span className="script-hl mt-2 text-[1.1em]">14th Street, DC</span>
        </h1>
        <p className="mt-6 max-w-xl animate-fade-up text-base leading-relaxed text-neutral-200 sm:text-lg" style={{ animationDelay: "0.2s" }}>
          Shop <CategoryLinks className="font-semibold text-white underline decoration-brand-lime decoration-2 underline-offset-4 hover:text-brand-lime" /> from our live menu. Order ahead, then come upstairs next to Chicken + Whiskey to pick up. Need a medical card? <Link to="/medical-card" className="font-semibold text-brand-lime underline underline-offset-4" data-testid="hero-medical-card-link">Self-certify in minutes</Link>.
        </p>
        <div className="mt-9 flex animate-fade-up flex-wrap gap-4" style={{ animationDelay: "0.3s" }}>
          <Link to="/shop-all" onClick={() => track("shop_now_click", { location: "hero" })} data-testid="hero-shop-now-button" className="btn-lime">Shop now</Link>
          <Link to="/location" data-testid="hero-directions-button" className="btn-outline-light">How to find us</Link>
        </div>
        <ul className="mt-12 flex animate-fade-up flex-wrap gap-x-8 gap-y-3" style={{ animationDelay: "0.4s" }}>
          {TRUST.map(([Icon, t]) => (
            <li key={t} className="flex items-center gap-2 text-xs font-semibold text-neutral-200"><Icon className="h-4 w-4 text-brand-lime" aria-hidden="true" />{t}</li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const WORDS = ["Up the stairs", "Next to Chicken + Whiskey", "1738 14th St NW", "Ring the bell", "Open till 11 Fri & Sat"];

export const InfoMarquee = () => (
  <Link to="/location" className="block overflow-hidden bg-brand-red py-3" data-testid="home-marquee" aria-label="We're upstairs at 1738 14th St NW next to Chicken + Whiskey — see directions">
    <div className="marquee flex w-max items-center gap-12" aria-hidden="true">
      {[...WORDS, ...WORDS, ...WORDS, ...WORDS].map((w, i) => (
        <span key={i} className="flex items-center gap-3 whitespace-nowrap font-display text-sm font-black tracking-tight text-white">
          <span className="text-brand-lime" aria-hidden="true">★</span>{w}
        </span>
      ))}
    </div>
  </Link>
);

export const FindUs = ({ headingLevel = "h2" }) => {
  const H = headingLevel;
  return (
    <section className="bg-brand-sand py-20 sm:py-28" data-testid="find-us-section" aria-labelledby="find-title">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="eyebrow">Finding us</p>
            <H id="find-title" className="mt-3 h-section">We're <span className="script-hl !text-brand-red text-[1.15em]">upstairs</span> — next to Chicken + Whiskey</H>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">{BUSINESS.addressLine}. Look for Chicken + Whiskey, take the first door on the left, walk up the stairs and ring the bell.</p>
          </div>
          <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("directions_click", { location: "find_us" })} data-testid="find-us-directions-button" className="btn-dark self-start md:self-auto">Get directions</a>
        </Reveal>
        <ol className="grid gap-4 md:grid-cols-3 lg:gap-6">
          {BUSINESS.arrival.map((s, i) => (
            <Reveal as="li" key={s.title} delay={0.08 * i} className="overflow-hidden rounded-2xl bg-white" >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={s.img} alt={s.alt} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-brand-lime font-display text-lg font-black">{i + 1}</span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-black uppercase tracking-wide">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};
